import React, { useState, useCallback, useRef, useMemo } from "react";
import {
  FaMicrochip,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaFileAlt,
  FaTimes,
  FaExclamationTriangle,
  FaTrashAlt,
  FaDownload,
  FaFileArchive,
} from "react-icons/fa";
import JSZip from "jszip";
import { Drawer } from "../shared";
import { TextInput } from "../atoms";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import { useAlert } from "../../context/AlertContext";
import {
  useGetHardwareVersionsQuery,
  useGetHardwarePresignedUploadUrlMutation,
  useConfirmHardwareUploadMutation,
  useLazyGetHardwareDownloadUrlQuery,
  useDeleteHardwareVersionMutation,
} from "../../store/storageRoles/hardwareApi";

// ─── Sabitler ───
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

// Kart kataloğu — id'ler FABRIKA PARÇA KODLARI (2026-08-12): tablet/istasyon
// güncellemeyi part.code ile sorgular. Kod = <aile><model no>.
//
// Model no ONLAR basamağı kart ailesini, BİRLER basamağı donanım varyantını
// gösterir. Fincanda ailesinde birler basamağı göz sayısını izler: 22 = 2 göz
// (debimetreli), 24 = 4 göz, 26 = 6 göz.
//
// YENİ VARYANT EKLERKEN: bu liste ile server'daki CARD_TYPES whitelist'i
// (hardware-update.controller.js) BİRLİKTE güncellenmeli. Sadece biri
// eklenirse paket ya dosya seçiminde ("kart kataloğunda bu model numarası
// yok") ya da yüklemede ("Gecersiz kart tipi", 400) reddedilir.
const CARD_TYPES = [
  { id: "CAK10", label: "Çayset Anakart", modelNo: 10, chip: "ATSAMD21J18A", color: "emerald" },
  { id: "FAK22", label: "Fincanda Anakart 2'li", modelNo: 22, chip: "ATSAMD21J18A", color: "amber" },
  { id: "FAK24", label: "Fincanda Anakart 4'lü", modelNo: 24, chip: "ATSAMD21J18A", color: "amber" },
  { id: "FAK26", label: "Fincanda Anakart 6'lı", modelNo: 26, chip: "ATSAMD21J18A", color: "amber" },
  { id: "ASK40", label: "Askı Kartı (J18A)", modelNo: 40, chip: "ATSAMD21J18A", color: "blue" },
  { id: "ASK41", label: "Askı Kartı (G18A)", modelNo: 41, chip: "ATSAMD21G18A", color: "blue" },
  { id: "DGR50", label: "Değirmen Kartı", modelNo: 50, chip: "ATSAMD21G18A", color: "purple" },
];

const cardByType = (cardType) => CARD_TYPES.find((c) => c.id === cardType);

// ─── SHA-256 hash hesapla (browser crypto API) ───
async function computeSha256(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ─── Zip'i aç, dosya listesi + firmware.json bilgilerini çıkar ───
async function inspectZip(file) {
  const zip = await JSZip.loadAsync(file);
  const entries = Object.values(zip.files).filter((f) => !f.dir);
  const names = entries.map((f) => f.name.split("/").pop());

  let firmwareInfo = null;
  const jsonEntry = entries.find((f) =>
    f.name.toLowerCase().endsWith(".json")
  );
  if (jsonEntry) {
    try {
      firmwareInfo = JSON.parse(await jsonEntry.async("string"));
    } catch {
      firmwareInfo = null;
    }
  }

  return { names, firmwareInfo };
}

// ─── Zip dosya adı sözleşmesi (build-firmware.sh çıktısı) ───
// Zorunlu 3 dosya: <kart>.bin, <kart>.json|firmware.json, <kart>_base_v<N>.hex
// Opsiyonel: <kart>_debug_v<N>.hex — başka dosya kabul edilmez.
function validateZipNames(names, cardId) {
  const isBin = (n) => n === `${cardId}.bin`;
  const isJson = (n) => n === `${cardId}.json` || n === "firmware.json";
  const isBaseHex = (n) => new RegExp(`^${cardId}_base_v\\d+\\.hex$`).test(n);
  const isDebugHex = (n) => new RegExp(`^${cardId}_debug_v\\d+\\.hex$`).test(n);

  const missing = [];
  if (!names.some(isBin)) missing.push(`${cardId}.bin`);
  if (!names.some(isJson)) missing.push(`${cardId}.json (veya firmware.json)`);
  if (!names.some(isBaseHex)) missing.push(`${cardId}_base_v<sürüm>.hex`);
  if (missing.length > 0) {
    return `Zip'te zorunlu dosyalar eksik veya isim formatı yanlış: ${missing.join(
      ", "
    )}. Paket build-firmware.sh ile üretilmelidir.`;
  }

  const unexpected = names.filter(
    (n) => !isBin(n) && !isJson(n) && !isBaseHex(n) && !isDebugHex(n)
  );
  if (unexpected.length > 0) {
    return `Zip'te beklenmeyen dosya(lar) var: ${unexpected.join(
      ", "
    )}. Sadece bin + json + base hex (+ debug hex) bulunmalıdır.`;
  }

  return "";
}

// ─── Dosya boyutu formatter ───
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

// ─── Tarih formatter ───
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Tespit Edilen Kart (salt-okunur) ───
// Kart tipi elle seçilmez — zip'teki firmware.json'ın model alanından
// otomatik tespit edilir; model yoksa dosya daha seçim aşamasında reddedilir.
const CARD_COLOR_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-300",
  amber: "bg-amber-100 text-amber-700 border-amber-300",
  blue: "bg-blue-100 text-blue-700 border-blue-300",
  purple: "bg-purple-100 text-purple-700 border-purple-300",
};

function DetectedCardInfo({ card }) {
  if (!card) return null;
  const colors = CARD_COLOR_CLASSES[card.color] || CARD_COLOR_CLASSES.blue;
  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        Kart Tipi{" "}
        <span className="text-[10px] font-medium text-gray-400">
          (firmware.json'dan otomatik)
        </span>
      </label>
      <div
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold shadow-sm ${colors}`}
      >
        <FaCheckCircle className="w-3 h-3" />
        <span>{card.label}</span>
        <span className="text-[10px] opacity-60">M{card.modelNo}</span>
        <span className="text-[10px] opacity-60">{card.chip}</span>
      </div>
    </div>
  );
}

// ─── Upload Form ───
function UploadForm() {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const [dragActive, setDragActive] = useState(false);

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [zipInfo, setZipInfo] = useState(null); // inspectZip sonucu
  const [cardType, setCardType] = useState("");
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");

  // Upload state
  const [uploadPhase, setUploadPhase] = useState("idle"); // idle | presigning | uploading | confirming
  const [uploadProgress, setUploadProgress] = useState(0);

  const [getPresignedUrl] = useGetHardwarePresignedUploadUrlMutation();
  const [confirmUpload] = useConfirmHardwareUploadMutation();

  const uploading = uploadPhase !== "idle";
  const selectedCard = cardByType(cardType);

  const processFile = useCallback(async (f) => {
    const reject = (message) => {
      setFileError(message);
      setFile(null);
      setZipInfo(null);
      setCardType("");
      setVersion("");
    };

    if (!f?.name?.toLowerCase().endsWith(".zip")) {
      setFileError("Sadece .zip dosyaları yüklenebilir");
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setFileError(`Dosya çok büyük (${formatFileSize(f.size)}). Maksimum 50 MB.`);
      return;
    }

    try {
      const info = await inspectZip(f);

      // Kart tipi firmware.json'daki model alanından tespit edilir —
      // model okunamayan paket yüklenemez
      const fw = info.firmwareInfo;
      if (!fw || fw.model == null) {
        return reject(
          "Zip içindeki firmware.json'dan model bilgisi okunamadı. Kart tipi doğrulanamayan paket yüklenemez — paketi güncel build-firmware.sh ile üretin."
        );
      }
      const match = CARD_TYPES.find((c) => c.modelNo === Number(fw.model));
      if (!match) {
        return reject(
          `firmware.json model ${fw.model} bildiriyor — kart kataloğunda bu model numarası yok.`
        );
      }

      const nameError = validateZipNames(info.names, match.id);
      if (nameError) return reject(nameError);

      setFileError("");
      setFile(f);
      setZipInfo(info);
      setCardType(match.id);
      if (fw.versionString) setVersion(String(fw.versionString));
      else if (fw.version != null) setVersion(String(fw.version));
    } catch {
      reject("Zip dosyası okunamadı — bozuk olabilir");
    }
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) processFile(droppedFile);
    },
    [processFile]
  );

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected) processFile(selected);
    e.target.value = "";
  };

  const resetForm = () => {
    setFile(null);
    setFileError("");
    setZipInfo(null);
    setCardType("");
    setVersion("");
    setNotes([]);
    setNoteInput("");
    setUploadPhase("idle");
    setUploadProgress(0);
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
  };

  const handleSubmit = async () => {
    if (!file) return showError("Zip dosyası seçiniz");
    if (!cardType) return showError("Kart tipi zip'ten tespit edilemedi — geçerli bir paket seçin");
    if (!version.trim()) return showError("Versiyon numarası zorunludur");

    try {
      // ── Phase 1: Presigned URL al ──
      setUploadPhase("presigning");
      setUploadProgress(0);

      const presignResult = await getPresignedUrl({
        token: auth.accessToken,
        cardType,
        version: version.trim(),
      }).unwrap();

      const { presignedUrl, storageKey } = presignResult.data;

      // ── Phase 2: Dosyayi direkt cloud'a yukle (XHR ile progress) ──
      setUploadPhase("uploading");

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload başarısız: HTTP ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error("Bağlantı hatası"));
        xhr.ontimeout = () => reject(new Error("Zaman aşımı"));

        xhr.open("PUT", presignedUrl);
        xhr.setRequestHeader("Content-Type", "application/zip");
        xhr.send(file);
      });

      xhrRef.current = null;

      // ── Phase 3: SHA-256 hesapla ve confirm et ──
      setUploadPhase("confirming");

      let sha256Hash = "";
      try {
        sha256Hash = await computeSha256(file);
      } catch {
        // SHA hesaplama basarisiz olsa da devam et
      }

      const fw = zipInfo?.firmwareInfo || {};

      await confirmUpload({
        token: auth.accessToken,
        storageKey,
        cardType,
        cardLabel: selectedCard?.label || "",
        modelNo: fw.model != null ? Number(fw.model) : selectedCard?.modelNo,
        chip: selectedCard?.chip || "",
        version: version.trim(),
        versionCode: fw.version != null ? Number(fw.version) : undefined,
        checksum: fw.checksum || "",
        files: JSON.stringify(zipInfo?.names || []),
        fileSize: file.size,
        sha256Hash,
        notes: notes.length > 0 ? JSON.stringify(notes) : undefined,
      }).unwrap();

      showSuccess("Hardware paketi başarıyla yüklendi");
      resetForm();
    } catch (err) {
      setUploadPhase("idle");
      setUploadProgress(0);
      if (xhrRef.current) {
        xhrRef.current.abort();
        xhrRef.current = null;
      }

      const status = err?.status;
      const msg = err?.data?.message || err?.message;

      if (status === 409) {
        showError(msg || "Bu kart için bu versiyon zaten mevcut");
      } else if (status === "FETCH_ERROR" || !status) {
        showError("Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.");
      } else {
        showError(msg || "Hardware paketi yüklenirken hata oluştu");
      }
    }
  };

  // Upload phase label
  const phaseLabel = {
    presigning: "URL alınıyor...",
    uploading: `Yükleniyor %${uploadProgress}...`,
    confirming: "Kaydediliyor...",
  };

  return (
    <div className="border border-gray-100 rounded-2xl bg-white shadow-sm overflow-hidden">
      {/* Collapse header */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-indigo-100 rounded-xl flex items-center justify-center">
            <FaCloudUploadAlt className="w-4 h-4 text-indigo-600" />
          </div>
          <span className="text-sm font-bold text-indigo-800">Yeni Hardware Yükle</span>
        </div>
        {expanded ? (
          <FaChevronUp className="w-3.5 h-3.5 text-indigo-500" />
        ) : (
          <FaChevronDown className="w-3.5 h-3.5 text-indigo-500" />
        )}
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Drag & Drop Area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-300 ${
              uploading ? "cursor-wait opacity-60" : "cursor-pointer"
            } ${
              fileError
                ? "border-red-300 bg-red-50/30"
                : dragActive
                  ? "border-indigo-400 bg-indigo-50/50 scale-[1.01]"
                  : file
                    ? "border-indigo-300 bg-indigo-50/30"
                    : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <FaFileArchive className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                </div>
                {!uploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setZipInfo(null);
                      setFileError("");
                      setCardType("");
                      setVersion("");
                    }}
                    className="ml-2 w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                  >
                    <FaTimes className="w-3 h-3 text-red-500" />
                  </button>
                )}
              </div>
            ) : (
              <>
                <FaCloudUploadAlt className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">
                  Zip dosyasını sürükleyin veya{" "}
                  <span className="text-indigo-600 font-semibold">tıklayın</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  build-firmware.sh zip'i: kart.bin + kart.json + kart_base_v&lt;N&gt;.hex — Maksimum 50 MB
                </p>
              </>
            )}
          </div>
          {fileError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-xl">
              <FaExclamationTriangle className="w-3 h-3 shrink-0" />
              <span>{fileError}</span>
            </div>
          )}

          {/* Zip içeriği önizleme */}
          {zipInfo && (
            <div className="px-3 py-2 bg-gray-50 rounded-xl space-y-1.5">
              <p className="text-[11px] font-bold text-gray-500">Zip İçeriği</p>
              <div className="flex flex-wrap gap-1.5">
                {zipInfo.names.map((name) => (
                  <span
                    key={name}
                    className="text-[10px] font-semibold bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-md"
                  >
                    {name}
                  </span>
                ))}
              </div>
              {zipInfo.firmwareInfo && (
                <p className="text-[11px] text-gray-500">
                  firmware.json → model{" "}
                  <span className="font-bold">{zipInfo.firmwareInfo.model ?? "—"}</span>
                  {" · "}v
                  <span className="font-bold">
                    {zipInfo.firmwareInfo.versionString ?? zipInfo.firmwareInfo.version ?? "—"}
                  </span>
                  {" · "}checksum{" "}
                  <span className="font-mono font-bold">{zipInfo.firmwareInfo.checksum ?? "—"}</span>
                </p>
              )}
            </div>
          )}

          {/* Kart Tipi — firmware.json'dan otomatik tespit, elle seçim yok */}
          <DetectedCardInfo card={selectedCard} />

          {/* Versiyon */}
          <TextInput
            label="Versiyon"
            placeholder="Örn: 1.9.5"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            required
          />

          {/* Notlar — madde ekleme */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Notlar</label>
            <div className="flex gap-2">
              <TextInput
                placeholder="Sürüm notu ekleyin..."
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && noteInput.trim()) {
                    e.preventDefault();
                    setNotes((prev) => [...prev, noteInput.trim()]);
                    setNoteInput("");
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (noteInput.trim()) {
                    setNotes((prev) => [...prev, noteInput.trim()]);
                    setNoteInput("");
                  }
                }}
                className="shrink-0 px-3 py-2 rounded-xl bg-indigo-100 text-indigo-700 text-xs font-semibold hover:bg-indigo-200 transition-colors"
              >
                Ekle
              </button>
            </div>
            {notes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {notes.map((note, idx) => (
                  <span
                    key={idx}
                    className="flex items-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg"
                  >
                    <span>•</span>
                    <span>{note}</span>
                    <button
                      type="button"
                      onClick={() => setNotes((prev) => prev.filter((_, i) => i !== idx))}
                      className="ml-0.5 w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center transition-colors"
                    >
                      <FaTimes className="w-2 h-2 text-gray-500 hover:text-red-500" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={uploading || !file || !cardType || !version.trim()}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-semibold text-sm transition-all duration-300 shadow-sm ${
              uploading || !file || !cardType || !version.trim()
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white hover:shadow-md active:scale-[0.98]"
            }`}
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{phaseLabel[uploadPhase] || "Yükleniyor..."}</span>
              </>
            ) : (
              <>
                <FaCloudUploadAlt className="w-4 h-4" />
                <span>Hardware Yükle</span>
              </>
            )}
          </button>

          {/* Progress bar — sadece uploading fazinda goster */}
          {uploadPhase === "uploading" && (
            <div className="space-y-1">
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 text-right">
                {formatFileSize(Math.round((file?.size || 0) * uploadProgress / 100))} / {formatFileSize(file?.size || 0)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Version Card ───
function VersionCard({
  version,
  isLatest,
  latestVersionLabel,
  onDelete,
  deleting,
  canManage,
}) {
  const { auth } = useAuth();
  const { showError } = useAlert();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Detaylar daraltilabilir: guncel paket acik gelir, eski surumler kapali.
  // Ayni kartin 4-5 eski surumu listede yan yana durunca dosya/not bloklari
  // sayfayi sisiriyordu; sahaya giden paket ustte ve acik kalsin.
  //
  // null = kullanici henuz dokunmadi -> isLatest'i takip eder. Yeni paket
  // yuklenince dunku guncel kart kendiliginden kapanir; kullanici bir kez
  // acip kapadiysa onun tercihi korunur.
  const [detailsOpenOverride, setDetailsOpen] = useState(null);
  const detailsOpen = detailsOpenOverride ?? isLatest;

  const [getDownloadUrl] = useLazyGetHardwareDownloadUrlQuery();

  const card = cardByType(version.cardType);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const result = await getDownloadUrl({
        token: auth.accessToken,
        id: version._id,
      }).unwrap();
      const { url, fileName } = result.data;
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || version.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      showError(err?.data?.message || "İndirme bağlantısı alınamadı");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={`border rounded-2xl transition-all duration-200 hover:shadow-md ${
        confirmOpen
          ? "border-red-200 bg-gradient-to-br from-red-50/30 to-white shadow-sm"
          : isLatest
            ? "border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white shadow-sm"
            : "border-gray-200 bg-gray-50/60"
      }`}
    >
      <div className="p-4">
        {/* Top row — tiklayinca detaylar daralir/acilir */}
        <div
          className={`flex items-center justify-between ${detailsOpen ? "mb-3" : ""}`}
        >
          <button
            type="button"
            onClick={() => setDetailsOpen(!detailsOpen)}
            className="flex items-center gap-2.5 text-left min-w-0"
            title={detailsOpen ? "Detayları gizle" : "Detayları göster"}
          >
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                isLatest ? "bg-emerald-100" : "bg-gray-200"
              }`}
            >
              <FaMicrochip
                className={`w-4 h-4 ${
                  isLatest ? "text-emerald-600" : "text-gray-400"
                }`}
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-sm font-bold ${
                    isLatest ? "text-gray-800" : "text-gray-500"
                  }`}
                >
                  {version.cardLabel || card?.label || version.cardType}
                </span>
                <span
                  className={`text-xs font-bold ${
                    isLatest ? "text-emerald-700" : "text-gray-400"
                  }`}
                >
                  v{version.version}
                </span>
                {isLatest ? (
                  <span className="flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-md">
                    <FaCheckCircle className="w-2.5 h-2.5" />
                    Güncel
                  </span>
                ) : (
                  <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-md">
                    Eski sürüm
                  </span>
                )}
                {/* Eski pakette "peki guncel hangisi" sorusu kartin uzerinde
                    cevaplansin — liste uzayinca yukari bakmak gerekmesin. */}
                {!isLatest && latestVersionLabel && (
                  <span className="text-[10px] font-semibold text-gray-400">
                    güncel: v{latestVersionLabel}
                  </span>
                )}
                {detailsOpen ? (
                  <FaChevronUp className="w-2.5 h-2.5 text-gray-400" />
                ) : (
                  <FaChevronDown className="w-2.5 h-2.5 text-gray-400" />
                )}
              </div>
              <p className="text-[11px] text-gray-400">
                {formatDate(version.createdInfo)}
              </p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">
              {formatFileSize(version.fileSize)}
            </span>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[11px] font-bold transition-colors disabled:opacity-50"
              title="Zip paketini indir"
            >
              {downloading ? (
                <div className="w-3 h-3 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
              ) : (
                <FaDownload className="w-3 h-3" />
              )}
              <span>İndir</span>
            </button>
            {canManage && (
              <button
                onClick={() => {
                  setDetailsOpen(true);
                  setConfirmOpen(true);
                }}
                disabled={confirmOpen || deleting}
                className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors disabled:opacity-50"
                title="Hardware Sil"
              >
                <FaTrashAlt className="w-3 h-3 text-red-500" />
              </button>
            )}
          </div>
        </div>

        {/* Detaylar — daraltilabilir bolum */}
        {detailsOpen && (
          <>
        {/* Kart bilgileri */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">
            Model {version.modelNo ?? card?.modelNo ?? "—"}
          </span>
          {(version.chip || card?.chip) && (
            <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">
              {version.chip || card?.chip}
            </span>
          )}
          {version.versionCode != null && (
            <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md">
              Kod {version.versionCode}
            </span>
          )}
          {version.checksum && (
            <span className="text-[10px] font-mono font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md">
              CRC {version.checksum}
            </span>
          )}
        </div>

        {/* Zip içeriği */}
        {version.files?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {version.files.map((name) => (
              <span
                key={name}
                className="text-[10px] font-medium bg-white border border-gray-200 text-gray-500 px-2 py-0.5 rounded-md"
              >
                {name}
              </span>
            ))}
          </div>
        )}

        {/* Notlar */}
        {version.notes?.length > 0 && (
          <div className="px-3 py-2 bg-gray-50 rounded-xl space-y-1">
            {version.notes.map((note, idx) => (
              <p
                key={idx}
                className="text-xs text-gray-600 leading-relaxed flex items-start gap-1.5"
              >
                <span className="text-gray-400 mt-px">•</span>
                <span>{note}</span>
              </p>
            ))}
          </div>
        )}

          </>
        )}

        {/* Inline silme onayi */}
        {confirmOpen && (
          <div className="mt-2 border-t border-red-100 pt-3 space-y-2">
            <div className="flex items-start gap-2 text-[11px] text-red-700 bg-red-50 px-3 py-2 rounded-xl">
              <FaExclamationTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              <span>
                Bu işlem geri alınamaz. Hardware paketi sunucudan ve
                veritabanından kalıcı olarak silinecektir.
              </span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => onDelete?.(version)}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Siliniyor...</span>
                  </>
                ) : (
                  <>
                    <FaTrashAlt className="w-2.5 h-2.5" />
                    <span>Sil</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Version List Skeleton ───
function VersionListSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border border-gray-100 rounded-2xl p-4 animate-pulse">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gray-200" />
              <div>
                <div className="w-24 h-4 bg-gray-200 rounded mb-1" />
                <div className="w-20 h-3 bg-gray-100 rounded" />
              </div>
            </div>
            <div className="w-14 h-6 bg-gray-200 rounded-lg" />
          </div>
          <div className="w-full h-8 bg-gray-50 rounded-xl" />
        </div>
      ))}
    </div>
  );
}

// ─── Main Drawer ───
export default function HardwareUpdateDrawer({ open, onClose }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canManage = hasPermission("devices.hardwareUpdate");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteHardwareVersion] = useDeleteHardwareVersionMutation();

  const {
    data: versionsData,
    isLoading,
    isFetching,
  } = useGetHardwareVersionsQuery(auth.accessToken, { skip: !open });

  const versions = useMemo(
    () => versionsData?.data || [],
    [versionsData?.data]
  );

  // Kart tipi basina GUNCEL paket. Olcut oncelikle sayisal versionCode
  // (firmware.json'daki version) — eski bir surum sonradan yuklenirse liste
  // sirasi ("createdInfo azalan") yaniltir ve yanlis kayit guncel gorunurdu.
  // versionCode gelmeyen eski kayitlarda listedeki ilk kayda dusulur.
  const latestByCard = useMemo(() => {
    const map = {};
    versions.forEach((v) => {
      const current = map[v.cardType];
      if (!current) {
        map[v.cardType] = v;
        return;
      }
      const a = v.versionCode;
      const b = current.versionCode;
      if (a != null && b != null && a > b) map[v.cardType] = v;
    });
    return map;
  }, [versions]);

  const handleDelete = async (version) => {
    setDeletingId(version._id);
    try {
      await deleteHardwareVersion({
        token: auth.accessToken,
        id: version._id,
      }).unwrap();
      showSuccess(
        `${version.cardLabel || version.cardType} v${version.version} başarıyla silindi`
      );
    } catch (err) {
      showError(err?.data?.message || "Hardware silinirken hata oluştu");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Hardware Yükleme Yönetimi"
      subtitle="Kart firmware paketlerini yönetin"
      titleIcon={<FaMicrochip className="w-5 h-5 text-indigo-500" />}
      width="md"
      headerGradient
    >
      <Drawer.Body className="p-4 space-y-5 bg-gray-50/30">
        {/* Upload Section — sadece devices.hardwareUpdate yetkisi olanlara */}
        {canManage && <UploadForm />}

        {/* Versions List */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FaFileAlt className="w-3.5 h-3.5 text-gray-400" />
            <h3 className="text-sm font-bold text-gray-700">
              Yüklenen Paketler
            </h3>
            {versions.length > 0 && (
              <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-md">
                {versions.length}
              </span>
            )}
          </div>

          {isLoading || isFetching ? (
            <VersionListSkeleton />
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <FaMicrochip className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400 font-medium">
                Henüz yüklenmiş bir hardware paketi yok
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((v) => (
                <VersionCard
                  key={v._id}
                  version={v}
                  isLatest={latestByCard[v.cardType]?._id === v._id}
                  latestVersionLabel={latestByCard[v.cardType]?.version}
                  onDelete={handleDelete}
                  deleting={deletingId === v._id}
                  canManage={canManage}
                />
              ))}
            </div>
          )}
        </div>
      </Drawer.Body>
    </Drawer>
  );
}
